import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container, Paper, Box, Typography, Button, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Card, CardContent, LinearProgress, Snackbar, Alert, Menu, MenuItem,
  CircularProgress, Grid, Chip, FormControl, InputLabel, Select,
  InputAdornment, Collapse
} from '@mui/material';
import {
  CloudUpload, Delete, Download, Edit, MoreVert, FolderOpen,
  Close, CheckCircle, Refresh, Visibility, Search, FilterList,
  ExpandMore, ExpandLess, GridView, ViewList
} from '@mui/icons-material';
import FilePreview from './FilePreview';
import './Arquivos.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function Arquivos() {
  const inputFileRef = useRef(null);
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [espacoInfo, setEspacoInfo] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, limit: 24, total: 0 });
  const [contadores, setContadores] = useState({ meus: 0, cliente: 0, aluguel: 0 });

  // Modal de renomeação
  const [renomeModal, setRenomeModal] = useState({ aberto: false, id: null, novoNome: '', tipo: 'arquivo' });
  const [novaPastaModal, setNovaPastaModal] = useState({ aberto: false, nomePasta: '' });

  // Menu de ações
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuArquivoId, setMenuArquivoId] = useState(null);
  const [menuPastaId, setMenuPastaId] = useState(null);

  // Upload múltiplo com progresso
  const [uploadProgresso, setUploadProgresso] = useState({});

  // Preview de arquivo
  const [previewAberto, setPreviewAberto] = useState(false);
  const [arquivoPreview, setArquivoPreview] = useState(null);

  // Navegação de pastas
  const [pastaAtual, setPastaAtual] = useState(null);
  const [pastaVirtual, setPastaVirtual] = useState(null);
  const [pastas, setPastas] = useState([]);

  // Pesquisa e filtros
  const [pesquisa, setPesquisa] = useState('');
  const [filtros, setFiltros] = useState({
    tipo: '', // 'imagem', 'documento', 'video', 'audio', 'outro'
    periodo: '', // 'hoje', 'semana', 'mes', 'ano'
    ordenacao: 'recente' // 'recente', 'antigo', 'nome', 'tamanho'
  });
  const [filtrosAberto, setFiltrosAberto] = useState(false);

  // Modo de visualização e seleção múltipla
  const [thumbnailView, setThumbnailView] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const arquivosFiltrados = useMemo(() => arquivos, [arquivos]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const token = localStorage.getItem('token');

  useEffect(() => {
    carregarEspacoInfo();
  }, []);

  useEffect(() => {
    carregarPastas();
  }, [pastaAtual]);

  useEffect(() => {
    carregarArquivos();
    setSelectedFileIds(new Set());
  }, [pagination.page, pagination.limit, pastaAtual, pastaVirtual, pesquisa, filtros.tipo, filtros.periodo, filtros.ordenacao]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [pastaAtual, pastaVirtual, pesquisa, filtros.tipo, filtros.periodo, filtros.ordenacao]);

  async function carregarDados() {
    await Promise.all([
      carregarPastas(),
      carregarArquivos(),
      carregarEspacoInfo()
    ]);
  }

  async function carregarPastas() {
    try {
      const params = pastaAtual ? `?pastaPaiId=${pastaAtual}` : '';
      const resp = await fetch(`${API_URL}/pastas${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setPastas(await resp.json());
      } else {
        const errData = await resp.json().catch(() => ({}));
        console.error(`Erro ao carregar pastas [${resp.status}]:`, errData);
        if (resp.status === 401 || resp.status === 403) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pastas:', error);
    }
  }

  async function carregarArquivos() {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pagination.page + 1),
        limit: String(pagination.limit),
        ordenacao: filtros.ordenacao
      });

      if (pastaVirtual) params.append('origem', pastaVirtual);
      if (pastaAtual) params.append('pastaAtual', pastaAtual);
      if (pesquisa.trim()) params.append('pesquisa', pesquisa.trim());
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.periodo) params.append('periodo', filtros.periodo);

      const resp = await fetch(`${API_URL}/arquivos/biblioteca?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const dados = await resp.json();
        if (Array.isArray(dados)) {
          setArquivos(dados);
          setPagination((prev) => ({ ...prev, total: dados.length }));
        } else {
          setArquivos(dados.itens || []);
          setPagination((prev) => ({
            ...prev,
            total: dados.pagination?.total || 0
          }));
          setContadores({
            meus: dados.contadores?.meus || 0,
            cliente: dados.contadores?.cliente || 0,
            aluguel: dados.contadores?.aluguel || 0
          });
        }
      } else {
        const errData = await resp.json().catch(() => ({}));
        console.error(`Erro ao carregar arquivos [${resp.status}]:`, errData);
        if (resp.status === 401 || resp.status === 403) {
          mostraSnackbar('Sessão expirada. Faça login novamente.', 'error');
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else {
          mostraSnackbar(`Erro ao carregar arquivos: ${errData.erro || errData.mensagem || resp.statusText}`, 'error');
        }
      }
    } catch (error) {
      mostraSnackbar(`Erro ao carregar arquivos: ${error.message}`, 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function carregarEspacoInfo() {
    try {
      const resp = await fetch(`${API_URL}/arquivos/espaco/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setEspacoInfo(await resp.json());
      } else {
        const errData = await resp.json().catch(() => ({}));
        console.error(`Erro ao carregar espaço [${resp.status}]:`, errData);
        if (resp.status === 401 || resp.status === 403) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Erro ao carregar espaço:', error);
    }
  }

  function mostraSnackbar(msg, severity = 'success') {
    setSnackbar({ open: true, message: msg, severity });
  }

  async function handleUpload(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      let sucessos = 0;
      let erros = 0;
      const fileIds = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileId = `file-${i}-${Date.now()}`;

        try {
          // Simular progresso
          setUploadProgresso(prev => ({ ...prev, [fileId]: { nome: file.name, percentCompleto: 0 } }));

          const formData = new FormData();
          formData.append('arquivo', file);
          if (pastaAtual) {
            formData.append('pastaPaiId', pastaAtual);
          }

          // Simular progresso gradual (0% -> 50% antes de enviar)
          for (let p = 0; p <= 50; p += 10) {
            setUploadProgresso(prev => ({ ...prev, [fileId]: { nome: file.name, percentCompleto: p } }));
            await new Promise(resolve => setTimeout(resolve, 50));
          }

          const resp = await fetch(`${API_URL}/arquivos/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });

          // Progresso 100%
          setUploadProgresso(prev => ({ ...prev, [fileId]: { nome: file.name, percentCompleto: 100 } }));

          if (resp.ok) {
            sucessos++;
            console.log(`✅ Upload ${file.name} concluído - status ${resp.status}`);
          } else {
            erros++;
            try {
              const err = await resp.json();
              console.error(`❌ Upload ${file.name} falhou [${resp.status}]:`, err.erro);
            } catch {
              console.error(`❌ Upload ${file.name} falhou [${resp.status}]: sem detalhes`);
            }
          }
        } catch (error) {
          erros++;
          console.error(`❌ Erro upload ${file.name}:`, error.message || error);
        }
      }

      // Aguardar 1s antes de atualizar (garantir que dados estão no servidor)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Limpar progresso e recarregar
      setUploadProgresso({});

      if (sucessos > 0) {
        mostraSnackbar(`✅ ${sucessos} arquivo(s) enviado(s)${erros > 0 ? ` (${erros} falhas)` : ''}`);
        await carregarArquivos();
        await carregarEspacoInfo();
      } else if (erros > 0) {
        mostraSnackbar(`❌ Erro ao enviar ${erros} arquivo(s)`, 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao enviar arquivo', 'error');
      console.error(error);
      setUploadProgresso({});
    } finally {
      setUploading(false);
      if (inputFileRef.current) inputFileRef.current.value = '';
    }
  }

  function abrirMenuAcoes(event, arquivoId) {
    setAnchorEl(event.currentTarget);
    setMenuArquivoId(arquivoId);
  }

  function fecharMenuAcoes() {
    setAnchorEl(null);
    setMenuArquivoId(null);
  }

  function abrirPreview(arquivo) {
    setArquivoPreview(arquivo);
    setPreviewAberto(true);
  }

  async function criarNovaPasta() {
    if (!novaPastaModal.nomePasta.trim()) {
      mostraSnackbar('Nome da pasta obrigatório', 'error');
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/pastas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: novaPastaModal.nomePasta,
          pastaPaiId: pastaAtual
        })
      });

      if (resp.ok) {
        mostraSnackbar('Pasta criada com sucesso');
        setNovaPastaModal({ aberto: false, nomePasta: '' });
        carregarPastas();
      } else {
        const err = await resp.json();
        mostraSnackbar(err.erro || 'Erro ao criar pasta', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao criar pasta', 'error');
      console.error(error);
    }
  }

  function entrarPasta(pastaId) {
    setPastaVirtual(null);
    setPastaAtual(pastaId);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }

  function sairPasta() {
    setPastaAtual(null);
    setPastaVirtual(null);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }

  function entrarPastaVirtual(tipo) {
    setPastaAtual(null);
    setPastaVirtual(tipo);
    setSelectedFileIds(new Set());
    setPagination((prev) => ({ ...prev, page: 0 }));
  }

  async function renomearPasta(pastaId) {
    if (!renomeModal.novoNome.trim()) {
      mostraSnackbar('Nome não pode estar vazio', 'error');
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/pastas/${pastaId}/renomear`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nome: renomeModal.novoNome })
      });

      if (resp.ok) {
        mostraSnackbar('Pasta renomeada com sucesso');
        carregarPastas();
        setRenomeModal({ aberto: false, id: null, novoNome: '', tipo: 'arquivo' });
      } else {
        mostraSnackbar('Erro ao renomear', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao renomear', 'error');
      console.error(error);
    }
  }

  async function deletarPasta(pastaId) {
    if (!window.confirm('Tem certeza? Isto também deletará todos os arquivos dentro.')) return;

    try {
      const resp = await fetch(`${API_URL}/pastas/${pastaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resp.ok) {
        mostraSnackbar('Pasta deletada');
        carregarPastas();
      } else {
        mostraSnackbar('Erro ao deletar', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao deletar', 'error');
      console.error(error);
    }
    fecharMenuAcoes();
  }

  function abrirRenomeModal(arquivo) {
    setRenomeModal({ aberto: true, id: arquivo.id, novoNome: arquivo.nome, tipo: 'arquivo' });
    fecharMenuAcoes();
  }

  async function salvarRenomeacao() {
    if (!renomeModal.novoNome.trim()) {
      mostraSnackbar('Nome não pode estar vazio', 'error');
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/arquivos/${renomeModal.id}/renomear`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nome: renomeModal.novoNome })
      });

      if (resp.ok) {
        mostraSnackbar('Arquivo renomeado com sucesso');
        carregarArquivos();
        setRenomeModal({ aberto: false, id: null, novoNome: '' });
      } else {
        mostraSnackbar('Erro ao renomear', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao renomear', 'error');
      console.error(error);
    }
  }

  async function deletarArquivo(arquivoId) {
    if (!window.confirm('Tem certeza que deseja deletar?')) return;

    try {
      const resp = await fetch(`${API_URL}/arquivos/${arquivoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (resp.ok) {
        mostraSnackbar('Arquivo deletado');
        carregarArquivos();
        carregarEspacoInfo();
      } else {
        mostraSnackbar('Erro ao deletar', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao deletar', 'error');
      console.error(error);
    }
    fecharMenuAcoes();
  }

  async function confirmarDeletarMultiplos() {
    if (selectedFileIds.size === 0) return;
    setConfirmDeleteOpen(true);
  }

  async function executarDeletarMultiplos() {
    const ids = Array.from(selectedFileIds);

    try {
      const resp = await fetch(`${API_URL}/arquivos`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids })
      });

      if (resp.ok) {
        const resultado = await resp.json();
        mostraSnackbar(`✅ ${resultado.sucessos} arquivo(s) deletado(s)${resultado.erros > 0 ? ` (${resultado.erros} falhas)` : ''}`);
        setSelectedFileIds(new Set());
        setConfirmDeleteOpen(false);
        carregarArquivos();
        carregarEspacoInfo();
      } else {
        const erro = await resp.json();
        mostraSnackbar(erro.erro || 'Erro ao deletar arquivos', 'error');
      }
    } catch (error) {
      mostraSnackbar('Erro ao deletar arquivos', 'error');
      console.error(error);
    }
  }

  function cancelarDeletarMultiplos() {
    setConfirmDeleteOpen(false);
  }

  function toggleSelectFile(fileId) {
    const arquivo = arquivosFiltrados.find(a => a.id === fileId);
    if (arquivo && arquivo.selecionavel === false) return;
    const newSelected = new Set(selectedFileIds);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFileIds(newSelected);
  }

  function toggleSelectAll() {
    const selecionaveis = arquivosFiltrados.filter(a => a.selecionavel !== false);
    if (selectedFileIds.size === selecionaveis.length) {
      setSelectedFileIds(new Set());
    } else {
      setSelectedFileIds(new Set(selecionaveis.map(a => a.id)));
    }
  }

  function formatarTamanho(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function formatarData(data) {
    return new Date(data).toLocaleDateString('pt-BR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  function obterTipoArquivo(nome) {
    const ext = nome.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'imagem';
    if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'].includes(ext)) return 'documento';
    if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac'].includes(ext)) return 'audio';
    return 'outro';
  }

  function filtrarArquivos(lista, pesquisa, filtros) {
    let resultado = [...lista];

    // Filtro por nome/pesquisa
    if (pesquisa.trim()) {
      const termo = pesquisa.toLowerCase();
      resultado = resultado.filter(a =>
        a.nome.toLowerCase().includes(termo) ||
        (a.clienteNome || '').toLowerCase().includes(termo) ||
        (a.pastaLabel || '').toLowerCase().includes(termo) ||
        (a.origemLabel || '').toLowerCase().includes(termo)
      );
    }

    // Filtro por tipo
    if (filtros.tipo) {
      resultado = resultado.filter(a => obterTipoArquivo(a.nome) === filtros.tipo);
    }

    // Filtro por período
    if (filtros.periodo) {
      const agora = new Date();
      resultado = resultado.filter(a => {
        const dataCriacao = new Date(a.criado);
        const diasDecorridos = Math.floor((agora - dataCriacao) / (1000 * 60 * 60 * 24));

        if (filtros.periodo === 'hoje') return diasDecorridos === 0;
        if (filtros.periodo === 'semana') return diasDecorridos <= 7;
        if (filtros.periodo === 'mes') return diasDecorridos <= 30;
        if (filtros.periodo === 'ano') return diasDecorridos <= 365;
        return true;
      });
    }

    // Ordenação
    if (filtros.ordenacao === 'recente') {
      resultado.sort((a, b) => new Date(b.criado) - new Date(a.criado));
    } else if (filtros.ordenacao === 'antigo') {
      resultado.sort((a, b) => new Date(a.criado) - new Date(b.criado));
    } else if (filtros.ordenacao === 'nome') {
      resultado.sort((a, b) => a.nome.localeCompare(b.nome));
    } else if (filtros.ordenacao === 'tamanho') {
      resultado.sort((a, b) => b.tamanho - a.tamanho);
    }

    return resultado;
  }

  function filtrarPorNavegacao(lista) {
    if (pastaVirtual) {
      return lista.filter(a => a.pastaVirtual === pastaVirtual);
    }
    if (pastaAtual) {
      return lista.filter(a => a.pastaVirtual === 'meus');
    }
    return lista;
  }

  function contarPorPastaVirtual(tipo) {
    if (tipo === 'clientes') return contadores.cliente || 0;
    if (tipo === 'alugueis') return contadores.aluguel || 0;
    return 0;
  }

  function abrirDownload(arquivo) {
    const url = arquivo.downloadUrl?.startsWith('http')
      ? arquivo.downloadUrl
      : arquivo.downloadUrl
        ? `${API_URL}${arquivo.downloadUrl}`
        : arquivo.url;
    window.open(url, '_blank');
  }

  const percentualUsado = espacoInfo ? parseFloat(espacoInfo.percentualUsado) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FolderOpen sx={{ fontSize: 32, color: '#FF8C00' }} />
          <div>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>Meus Arquivos</Typography>
            <Typography variant="body2" color="textSecondary">Gerencie seus arquivos no espaço compartilhado</Typography>
          </div>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setNovaPastaModal({ ...novaPastaModal, aberto: true })}
            sx={{ borderColor: '#FF8C00', color: '#FF8C00' }}
          >
            📁 Nova Pasta
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => inputFileRef.current?.click()}
            disabled={uploading}
            sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
          >
            {uploading ? 'Enviando...' : 'Enviar Arquivo'}
          </Button>
          <Tooltip title={thumbnailView ? 'Visualização de lista' : 'Visualização em miniatura'}>
            <IconButton
              onClick={() => setThumbnailView(!thumbnailView)}
              sx={{ color: thumbnailView ? '#FF8C00' : '#999' }}
            >
              {thumbnailView ? <ViewList /> : <GridView />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton onClick={() => carregarDados()}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Resetar espaço (DEV)">
            <IconButton
              size="small"
              onClick={() => {
                if (window.confirm('Deletar TODOS os arquivos e pastas?')) {
                  fetch(`${API_URL}/arquivos/dev/resetar-espaco`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                  }).then(() => {
                    mostraSnackbar('Espaço resetado');
                    carregarDados();
                  }).catch(e => mostraSnackbar('Erro ao resetar', 'error'));
                }
              }}
              sx={{ color: '#ccc', '&:hover': { color: '#FF8C00' } }}
            >
              ↻
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <input
        ref={inputFileRef}
        type="file"
        hidden
        multiple
        onChange={handleUpload}
      />

      {/* Info Espaço */}
      {espacoInfo && (
        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                <strong>{espacoInfo.usadoMB} MB</strong> de <strong>{espacoInfo.limiteMB} MB</strong> usados
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {espacoInfo.disponivelMB} MB disponível
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentualUsado}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: percentualUsado > 80 ? '#FF6B6B' : '#FF8C00',
                  borderRadius: 4
                }
              }}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {percentualUsado.toFixed(1)}% de {espacoInfo.limiteMB} MB
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Breadcrumb */}
      {pastaAtual && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" onClick={sairPasta} sx={{ color: '#FF8C00' }}>
            ⬅️ Voltar
          </Button>
          <Typography variant="body2" color="textSecondary">
            Pasta atual: ID {pastaAtual}
          </Typography>
        </Box>
      )}

      {pastaVirtual && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" onClick={sairPasta} sx={{ color: '#FF8C00' }}>
            Voltar
          </Button>
          <Typography variant="body2" color="textSecondary">
            Pasta atual: {pastaVirtual === 'clientes' ? 'Clientes' : 'Aluguéis'}
          </Typography>
        </Box>
      )}

      {/* Progresso de Upload */}
      {Object.keys(uploadProgresso).length > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#fff3e0', borderLeft: '4px solid #FF8C00' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              📤 Enviando {Object.keys(uploadProgresso).length} arquivo(s)...
            </Typography>
            {Object.entries(uploadProgresso).map(([fileId, prog]) => (
              <Box key={fileId} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{prog.nome}</Typography>
                  <Typography variant="caption" color="textSecondary">{prog.percentCompleto.toFixed(0)}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={prog.percentCompleto}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#FF8C00', borderRadius: 3 }
                  }}
                />
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Barra de Pesquisa e Filtros */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField
            placeholder="🔍 Pesquisar por nome..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#FF8C00' }} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: '400px' }}
          />

          <Button
            variant={filtrosAberto ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setFiltrosAberto(!filtrosAberto)}
            sx={{
              borderColor: '#FF8C00',
              color: filtrosAberto ? '#fff' : '#FF8C00',
              backgroundColor: filtrosAberto ? '#FF8C00' : 'transparent',
              '&:hover': { backgroundColor: filtrosAberto ? '#E67E00' : 'rgba(255,140,0,0.1)' }
            }}
          >
            Filtros
          </Button>

          {(pesquisa || filtros.tipo || filtros.periodo) && (
            <Button size="small" onClick={() => {
              setPesquisa('');
              setFiltros({ tipo: '', periodo: '', ordenacao: 'recente' });
            }}>
              Limpar filtros
            </Button>
          )}
        </Box>

        {/* Painel de Filtros */}
        <Collapse in={filtrosAberto}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #ddd', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <FormControl size="small">
              <InputLabel>Tipo de Arquivo</InputLabel>
              <Select
                value={filtros.tipo}
                label="Tipo de Arquivo"
                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
              >
                <MenuItem value="">Todos os tipos</MenuItem>
                <MenuItem value="imagem">📷 Imagens</MenuItem>
                <MenuItem value="documento">📄 Documentos</MenuItem>
                <MenuItem value="video">🎬 Vídeos</MenuItem>
                <MenuItem value="audio">🎵 Áudios</MenuItem>
                <MenuItem value="outro">📦 Outros</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Período</InputLabel>
              <Select
                value={filtros.periodo}
                label="Período"
                onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })}
              >
                <MenuItem value="">Todos os períodos</MenuItem>
                <MenuItem value="hoje">📅 Hoje</MenuItem>
                <MenuItem value="semana">📆 Últimos 7 dias</MenuItem>
                <MenuItem value="mes">📊 Últimos 30 dias</MenuItem>
                <MenuItem value="ano">📈 Último ano</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Ordenar por</InputLabel>
              <Select
                value={filtros.ordenacao}
                label="Ordenar por"
                onChange={(e) => setFiltros({ ...filtros, ordenacao: e.target.value })}
              >
                <MenuItem value="recente">🕐 Mais recentes</MenuItem>
                <MenuItem value="antigo">📌 Mais antigos</MenuItem>
                <MenuItem value="nome">🔤 Nome (A-Z)</MenuItem>
                <MenuItem value="tamanho">📏 Maior tamanho</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {pagination.total !== arquivosFiltrados.length && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Mostrando {arquivosFiltrados.length} de {pagination.total} arquivo(s) encontrado(s)
          </Typography>
        )}
      </Paper>

      {/* Grid de Pastas */}
      {!pastaAtual && !pastaVirtual && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Pastas do sistema</Typography>
          <Grid container spacing={2}>
            {[
              { id: 'clientes', nome: 'Clientes', total: contarPorPastaVirtual('clientes') },
              { id: 'alugueis', nome: 'Aluguéis', total: contarPorPastaVirtual('alugueis') }
            ].map(pasta => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={pasta.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f9f9f9', boxShadow: 2 },
                    transition: 'all 0.2s'
                  }}
                  onClick={() => entrarPastaVirtual(pasta.id)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FolderOpen sx={{ color: '#FF8C00', fontSize: 26 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{pasta.nome}</Typography>
                      <Typography variant="caption" color="textSecondary">{pasta.total} arquivo(s)</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {pastas.length > 0 && !pastaVirtual && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Pastas ({pastas.length})</Typography>
          <Grid container spacing={2}>
            {pastas.map(pasta => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={pasta.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#f9f9f9', boxShadow: 2 },
                    transition: 'all 0.2s'
                  }}
                  onClick={() => entrarPasta(pasta.id)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <FolderOpen sx={{ color: '#FF8C00', fontSize: 24 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                        {pasta.nome}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setMenuPastaId(pasta.id);
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl && menuPastaId === pasta.id)}
                      onClose={fecharMenuAcoes}
                    >
                      <MenuItem onClick={() => {
                        setRenomeModal({ aberto: true, id: pasta.id, novoNome: pasta.nome, tipo: 'pasta' });
                        fecharMenuAcoes();
                      }}>
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Renomear
                      </MenuItem>
                      <MenuItem onClick={() => deletarPasta(pasta.id)} sx={{ color: '#FF6B6B' }}>
                        <Delete fontSize="small" sx={{ mr: 1 }} /> Deletar
                      </MenuItem>
                    </Menu>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Barra de Seleção Múltipla */}
      {selectedFileIds.size > 0 && (
        <Paper sx={{ mb: 2, p: 2, backgroundColor: '#fff3e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedFileIds.size} arquivo(s) selecionado(s)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => setSelectedFileIds(new Set())}>
              Desselecionar tudo
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={confirmarDeletarMultiplos}
            >
              Deletar selecionados
            </Button>
          </Box>
        </Paper>
      )}

      {/* Tabela/Grid de Arquivos */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Arquivos ({pagination.total})</Typography>
      </Box>

      {loading ? (
        <Paper sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Paper>
      ) : arquivosFiltrados.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FolderOpen sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography color="textSecondary">Nenhum arquivo encontrado</Typography>
          <Typography variant="body2" color="textSecondary">Use a pesquisa ou envie um novo arquivo</Typography>
        </Paper>
      ) : thumbnailView ? (
        // GRID VIEW (Miniaturas)
        <Grid container spacing={2}>
          {arquivosFiltrados.map((arquivo) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={arquivo.bibliotecaId || arquivo.id}>
              <Card
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: selectedFileIds.has(arquivo.id) ? '2px solid #FF8C00' : '1px solid #ddd',
                  backgroundColor: selectedFileIds.has(arquivo.id) ? '#fff8f0' : '#fff',
                  transition: 'all 0.2s',
                  '&:hover': { boxShadow: 3 }
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 2
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedFileIds.has(arquivo.id)}
                    disabled={arquivo.selecionavel === false}
                    onChange={() => toggleSelectFile(arquivo.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      accentColor: '#FF8C00'
                    }}
                  />
                </Box>

                <CardContent
                  onClick={() => abrirPreview(arquivo)}
                  sx={{ pb: 1 }}
                >
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Visibility sx={{ color: '#FF8C00', fontSize: 40 }} />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 1
                    }}
                  >
                    {arquivo.nome}
                  </Typography>
                  <Chip
                    label={obterTipoArquivo(arquivo.nome)}
                    size="small"
                    sx={{
                      height: '20px',
                      fontSize: '11px',
                      backgroundColor: obterTipoArquivo(arquivo.nome) === 'imagem' ? '#e1f5fe' :
                        obterTipoArquivo(arquivo.nome) === 'documento' ? '#f3e5f5' :
                        obterTipoArquivo(arquivo.nome) === 'video' ? '#fff3e0' :
                        obterTipoArquivo(arquivo.nome) === 'audio' ? '#f0f4c3' : '#f5f5f5'
                    }}
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    {arquivo.tamanho ? formatarTamanho(arquivo.tamanho) : arquivo.origemLabel}
                  </Typography>
                  {arquivo.pastaLabel && arquivo.pastaVirtual !== 'meus' && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                      {arquivo.pastaLabel}
                    </Typography>
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {formatarData(arquivo.criado)}
                  </Typography>
                </CardContent>

                <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                  <Tooltip title="Preview">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirPreview(arquivo);
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirDownload(arquivo);
                      }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {arquivo.editavel !== false && (
                    <Tooltip title="Mais ações">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirMenuAcoes(e, arquivo.id);
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl && menuArquivoId === arquivo.id)}
                    onClose={fecharMenuAcoes}
                  >
                    <MenuItem onClick={() => abrirRenomeModal(arquivo)}>
                      <Edit fontSize="small" sx={{ mr: 1 }} /> Renomear
                    </MenuItem>
                    <MenuItem onClick={() => deletarArquivo(arquivo.id)} sx={{ color: '#FF6B6B' }}>
                      <Delete fontSize="small" sx={{ mr: 1 }} /> Deletar
                    </MenuItem>
                  </Menu>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // LIST VIEW (Tabela)
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ width: 50, fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={selectedFileIds.size > 0 && selectedFileIds.size === arquivosFiltrados.filter(a => a.selecionavel !== false).length}
                    onChange={toggleSelectAll}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: 'pointer',
                      accentColor: '#FF8C00'
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Nome do Arquivo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Tamanho</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {arquivosFiltrados.map((arquivo) => (
                <TableRow
                  key={arquivo.bibliotecaId || arquivo.id}
                  hover
                  sx={{
                    backgroundColor: selectedFileIds.has(arquivo.id) ? '#fff8f0' : 'transparent'
                  }}
                >
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedFileIds.has(arquivo.id)}
                      disabled={arquivo.selecionavel === false}
                      onChange={() => toggleSelectFile(arquivo.id)}
                      style={{
                        width: 18,
                        height: 18,
                        cursor: 'pointer',
                        accentColor: '#FF8C00'
                      }}
                    />
                  </TableCell>
                  <TableCell
                    onClick={() => abrirPreview(arquivo)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#f0f0f0' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Visibility sx={{ color: '#FF8C00', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#0066cc', textDecoration: 'underline' }}>
                          {arquivo.nome}
                        </Typography>
                        <Chip
                          label={arquivo.origemLabel || obterTipoArquivo(arquivo.nome)}
                          size="small"
                          sx={{
                            mt: 0.5,
                            height: '20px',
                            fontSize: '11px',
                            backgroundColor: obterTipoArquivo(arquivo.nome) === 'imagem' ? '#e1f5fe' :
                              obterTipoArquivo(arquivo.nome) === 'documento' ? '#f3e5f5' :
                              obterTipoArquivo(arquivo.nome) === 'video' ? '#fff3e0' :
                              obterTipoArquivo(arquivo.nome) === 'audio' ? '#f0f4c3' : '#f5f5f5'
                          }}
                        />
                        {arquivo.pastaLabel && arquivo.pastaVirtual !== 'meus' && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            {arquivo.pastaLabel}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{arquivo.tamanho ? formatarTamanho(arquivo.tamanho) : '-'}</TableCell>
                  <TableCell align="center">
                    <Typography variant="caption">{formatarData(arquivo.criado)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    {arquivo.editavel === false ? (
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => abrirDownload(arquivo)}>
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Mais ações">
                        <IconButton size="small" onClick={(e) => abrirMenuAcoes(e, arquivo.id)}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl && menuArquivoId === arquivo.id)}
                      onClose={fecharMenuAcoes}
                    >
                      <MenuItem onClick={() => abrirDownload(arquivo)}>
                        <Download fontSize="small" sx={{ mr: 1 }} /> Download
                      </MenuItem>
                      <MenuItem onClick={() => abrirRenomeModal(arquivo)}>
                        <Edit fontSize="small" sx={{ mr: 1 }} /> Renomear
                      </MenuItem>
                      <MenuItem onClick={() => deletarArquivo(arquivo.id)} sx={{ color: '#FF6B6B' }}>
                        <Delete fontSize="small" sx={{ mr: 1 }} /> Deletar
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && pagination.total > 0 && (
        <Paper sx={{ mt: 2 }}>
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            rowsPerPage={pagination.limit}
            rowsPerPageOptions={[12, 24, 48, 96]}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, novaPagina) => {
              setPagination((prev) => ({ ...prev, page: novaPagina }));
            }}
            onRowsPerPageChange={(event) => {
              setPagination({
                page: 0,
                limit: Number.parseInt(event.target.value, 10),
                total: pagination.total
              });
            }}
          />
        </Paper>
      )}

      {/* Modal Renomeação (Arquivo ou Pasta) */}
      <Dialog open={renomeModal.aberto} onClose={() => setRenomeModal({ aberto: false, id: null, novoNome: '', tipo: 'arquivo' })} maxWidth="sm" fullWidth>
        <DialogTitle>Renomear {renomeModal.tipo === 'pasta' ? 'Pasta' : 'Arquivo'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Novo nome"
            value={renomeModal.novoNome}
            onChange={(e) => setRenomeModal({ ...renomeModal, novoNome: e.target.value })}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenomeModal({ aberto: false, id: null, novoNome: '', tipo: 'arquivo' })}>Cancelar</Button>
          <Button
            onClick={() => {
              if (renomeModal.tipo === 'pasta') {
                renomearPasta(renomeModal.id);
              } else {
                salvarRenomeacao();
              }
            }}
            variant="contained"
            sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Nova Pasta */}
      <Dialog open={novaPastaModal.aberto} onClose={() => setNovaPastaModal({ aberto: false, nomePasta: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Criar Nova Pasta</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Nome da pasta"
            value={novaPastaModal.nomePasta}
            onChange={(e) => setNovaPastaModal({ ...novaPastaModal, nomePasta: e.target.value })}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                criarNovaPasta();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovaPastaModal({ aberto: false, nomePasta: '' })}>Cancelar</Button>
          <Button onClick={criarNovaPasta} variant="contained" sx={{ backgroundColor: '#FF8C00', '&:hover': { backgroundColor: '#E67E00' } }}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview de Arquivo */}
      <FilePreview
        aberto={previewAberto}
        arquivo={arquivoPreview}
        onClose={() => setPreviewAberto(false)}
      />

      {/* Dialog de Confirmação - Deletar Múltiplos */}
      <Dialog open={confirmDeleteOpen} onClose={cancelarDeletarMultiplos} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>🗑️ Deletar Arquivos?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            Tem certeza que deseja deletar <strong>{selectedFileIds.size} arquivo(s)</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ⚠️ Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelarDeletarMultiplos}>Cancelar</Button>
          <Button
            onClick={executarDeletarMultiplos}
            variant="contained"
            color="error"
            startIcon={<Delete />}
          >
            Deletar {selectedFileIds.size} arquivo(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
